// Every team's week in order (B-385): who is scoring the most this week,
// whoever they play. The games say who is beating whom; this says where each
// score stands in the league, the high score at the top, and where the
// league's median falls when the league plays it (Q3).

import type { MatchupSummary, TeamRef } from '@fsp/domain';
import { useLeagueBase } from '../../data/hooks';
import { useAtLeast } from '../../data/detail';
import { TeamMark } from '../../kit';
import { Panel } from '../../kit/Panel';
import { Colored, Cover, Meter } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { TeamName } from '../shared/Names';
import { num } from '../shared/notComputed';

interface Entry {
  team: TeamRef;
  score: number;
  projected: number | null;
  matchupId: string;
}

/**
 * Whether the list has anything to say: a week with games, ordered by its
 * scores once anybody has one; until then by projected finish, which needs a
 * level that shows projections and a projection to order by. A week with
 * none worked out (a reference store, a best-ball side nobody can total) has
 * no order to give, and "Projected, highest first" over a column of dashes
 * would claim one.
 */
export function weekScoresShown(matchups: readonly MatchupSummary[], standard: boolean): boolean {
  if (matchups.length === 0) return false;
  if (matchups.some((m) => m.home.score !== 0 || m.away.score !== 0)) return true;
  return standard && matchups.some((m) => m.home.projected !== null || m.away.projected !== null);
}

/**
 * The median as a line can say it without matching a score on the wrong side
 * of it: to the tenth when it is a tenth, else to the hundredth. 100.25 to
 * the tenth is 100.3, and a 100.3 above a line marked 100.3 reads as a tie
 * scored a win.
 */
function medianText(median: number): string {
  const tenth = median.toFixed(1);
  return Math.abs(Number(tenth) - median) < 1e-9 ? tenth : median.toFixed(2);
}

export function WeekScores({
  matchups,
  median,
}: {
  matchups: MatchupSummary[];
  /** The week's league median, in a league that plays it (Q3); null otherwise. */
  median: number | null;
}) {
  const base = useLeagueBase();
  const standard = useAtLeast('standard');
  const entries: Entry[] = matchups.flatMap((m) =>
    [m.home, m.away].map((s) => ({
      team: s.team,
      score: s.score,
      projected: s.projected,
      matchupId: m.id,
    })),
  );
  if (!weekScoresShown(matchups, standard)) return null;
  // BY SCORE ONCE ANYBODY HAS ONE; until then by projected finish, which is
  // the only order the week has yet (a week before kickoff, or one whose
  // lines have not landed). An order by numbers the level hides would be an
  // order nobody can read, so at Simple a week with no scores has no list.
  const byScore = entries.some((e) => e.score !== 0);
  const value = (e: Entry) => (byScore ? e.score : e.projected);
  const sorted = [...entries].sort(
    (a, b) =>
      (value(b) ?? -Infinity) - (value(a) ?? -Infinity) ||
      (b.projected ?? -Infinity) - (a.projected ?? -Infinity) ||
      a.team.name.localeCompare(b.team.name),
  );
  // Level scores share a place.
  const places = sorted.map((e, i) => {
    const first = sorted.findIndex((x) => value(x) === value(e));
    return first === -1 ? i + 1 : first + 1;
  });
  const top = Math.max(0, ...sorted.map((e) => value(e) ?? 0));
  const final = matchups.every((m) => m.status === 'final');
  // A projection beside a score only where somebody has one to show.
  const projections = entries.some((e) => e.projected !== null);
  // THE MEDIAN'S LINE, between the last score above it and the first that is
  // not: above it wins the week's game against it (Q3, B-359). A number, so
  // from Standard; only once the list is by score.
  const cutAt =
    median !== null && byScore && standard ? sorted.findIndex((e) => e.score <= median) : -1;
  return (
    <Panel
      title={byScore ? 'Scores in order' : 'Projected, highest first'}
      meta={
        // A count, so from Standard. Inline in a span: the panel's tools are
        // a flex row, which made the Term a block, and its padding grew the
        // head past its neighbor's.
        !standard ? undefined : (
          <span className="fsp-muted fsp-sb-count">
            <Term
              tip={
                byScore
                  ? 'Every team’s score this week, highest first, whoever they play. Level scores are in order of projected finish.'
                  : 'Nobody has a score yet, so every team is in order of its projected finish this week.'
              }
            >
              {/* Its words lead its name, so what is read out starts with what
                is seen (axe's label-content-name-mismatch). */}
              {`${entries.length} teams`}
            </Term>
          </span>
        )
      }
    >
      <ol className="fsp-ws" aria-label={byScore ? 'Scores in order' : 'Projected, highest first'}>
        {sorted.map((e, i) => {
          const v = value(e);
          const share = top > 0 && v !== null ? Math.max(0, v) / top : 0;
          return (
            <li key={e.team.id} className="fsp-ws-item">
              {i === cutAt ? (
                // A separator's words are not read out (its children are
                // presentational), so it is named with them.
                <div
                  className="fsp-ws-median"
                  role="separator"
                  aria-label={`League median ${medianText(median!)}${final ? '' : ' so far'}`}
                >
                  <span>
                    League median <span className="fsp-num">{medianText(median!)}</span>
                    {final ? '' : ' so far'}
                  </span>
                </div>
              ) : null}
              <Colored
                className="fsp-ws-row fsp-cover-host"
                colors={{ '--team': e.team.colors.primary }}
                data-tone={e.team.isMine ? 'mine' : undefined}
              >
                <Cover
                  to={`${base}/matchup/${e.matchupId}`}
                  label={`${e.team.name}, ${places[i]} of ${sorted.length}: open the matchup`}
                />
                <span className="fsp-ws-place fsp-num">{places[i]}</span>
                <TeamMark team={e.team} size={20} />
                <span className="fsp-ws-who">
                  <span className="fsp-ws-name">
                    <TeamName team={e.team} />
                  </span>
                  {/* How far each score is along to the top one: the shape of
                      the week without reading a number. */}
                  <span className="fsp-ws-bar">
                    <Meter value={share} />
                  </span>
                </span>
                <span className="fsp-ws-nums">
                  <span className="fsp-ws-score fsp-num">
                    {byScore ? e.score.toFixed(1) : num(e.projected)}
                  </span>
                  {/* Where the score is headed while the week is played, from
                      Standard; not once it is final, where it is the score. */}
                  {byScore && standard && !final && projections ? (
                    <span className="fsp-ws-proj fsp-num">proj {num(e.projected)}</span>
                  ) : null}
                </span>
              </Colored>
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}
